import React, { useMemo } from 'react';
import { Box, Button, Radio, Divider } from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { DistributorTypeFilter, DivisionFilter } from './DistributorFilters';
import { newReportConfig } from '../mdmReportsUtils';
import { Option } from './CompactFilterDropdown';
import SalesHierarchyFilter from '../utils/SalesHierarchyFilter';
import GeographicalHierarchyFilter from '../utils/GeographicalHierarchyFilter';
import HierarchyDropdown from './HierarchyDropdown';
import { PathSummaryItem } from '../utils/hierarchyHelpers';
import DistributorFilter from '../utils/DistributorFilter';

interface TopFilterBarProps {
  // Config
  selectedReport: newReportConfig | null;
  
  // State
  filters: Record<string, any>;
  optionsMap: Record<string, Option[]>;
  loadingMap: Record<string, boolean>;
  
  // Base Handlers
  onFilterChange: (key: string, value: any) => void;
  onMultiFilterChange: (key: string, values: string[]) => void;

  // Sales hierarchy handlers
  onSalesLevelChange: (value: string | null) => void;
  onSalesValueChange: (values: string[]) => void;
  onSalesLoadLevels: () => void;
  onSalesNextLevel?: () => void;
  onSalesResetAll?: () => void;
  onSalesDropdownClose?: () => void;

  // Geographical hierarchy handlers
  onGeoLevelChange: (value: string | null) => void;
  onGeoValueChange: (values: string[]) => void;
  onGeoLoadLevels: () => void;
  onGeoNextLevel?: () => void;
  onGeoResetAll?: () => void;
  onGeoDropdownClose?: () => void;
  geoIsLastLevel?: boolean;

  // Selection counts for display (optional)
  salesDrillDownPath?: Array<{ level: string; value: string }>;
  geoDrillDownPath?: Array<{ level: string; value: string }>;
  distributorSource: "geographical" | "sales";
  onDistributorSourceChange: (source: "geographical" | "sales") => void;
  
  // Distributor filter handlers
  onLoadDistributorOptions?: (fieldKey: string) => void;
  shouldDisableDistributorFilter?: boolean;
  distributorDisabledReason?: string;
  searchTextMap?: Record<string, string>;
  onFilterInputChange?: (key: string, inputValue: string) => void;
  
  // Loading state
  isHierarchySyncing?: boolean;
  // Reset handler
  onReset?: () => void;
}

// Helper function to summarize drill-down path
const summarizeDrillDownPath = (path: Array<{ level: string; value: string }>): PathSummaryItem[] => {
  const summary: PathSummaryItem[] = [];
  path.forEach((item, index) => {
    const last = summary[summary.length - 1];
    if (last && last.level === item.level) {
      last.count += 1;
    } else {
      summary.push({ 
        level: item.level, 
        count: 1, 
        startIndex: index,
        firstValue: item.value
      });
    }
  });
  return summary;
};

const TopFilterBar: React.FC<TopFilterBarProps> = ({
  selectedReport,
  filters,
  optionsMap,
  loadingMap,
  onFilterChange,
  onMultiFilterChange,
  onSalesLevelChange,
  onSalesValueChange,
  onSalesLoadLevels,
  onSalesNextLevel,
  onSalesResetAll,
  onSalesDropdownClose,
  onGeoLevelChange,
  onGeoValueChange,
  onGeoLoadLevels,
  onGeoNextLevel,
  onGeoResetAll,
  onGeoDropdownClose,
  geoIsLastLevel,
  salesDrillDownPath = [],
  geoDrillDownPath = [],
  distributorSource,
  onDistributorSourceChange,
  onLoadDistributorOptions,
  shouldDisableDistributorFilter = false,
  distributorDisabledReason,
  searchTextMap = {},
  onFilterInputChange,
  isHierarchySyncing = false,
  onReset
}) => {
  
  // Helper to extract values
  const getFilterValue = (key: string) => {
     return filters[key]?.[0] || null;
  };
  const getFilterValues = (key: string) => {
    return filters[key] || [];
  };
  
  const getOptions = (key: string) => {
    return optionsMap[key] || [];
  };

  const isLoading = (key: string) => {
    return loadingMap[key] || false;
  };

  // Calculate selection counts
  const geoSelectionCount = geoDrillDownPath.length + 
    (selectedReport?.geographicalHierarchyFilter?.levelFilterField 
      ? (filters[filters[selectedReport.geographicalHierarchyFilter.levelFilterField]?.[0]]?.length || 0)
      : 0);

  const salesSelectionCount = salesDrillDownPath.length + 
    (selectedReport?.salesHierarchyFilter?.levelFilterField 
      ? (filters[filters[selectedReport.salesHierarchyFilter.levelFilterField]?.[0]]?.length || 0)
      : 0);

  // Generate path summaries for display
  const geoPathSummary = useMemo(() => summarizeDrillDownPath(geoDrillDownPath), [geoDrillDownPath]);
  const salesPathSummary = useMemo(() => summarizeDrillDownPath(salesDrillDownPath), [salesDrillDownPath]);

  const geoEnabled = !!selectedReport?.geographicalHierarchyFilter?.enabled;
  const salesEnabled = !!selectedReport?.salesHierarchyFilter?.enabled;
  const showDistributorToggle = geoEnabled && salesEnabled;
  const isGeoActive = geoEnabled && (!showDistributorToggle || distributorSource === "geographical");
  const isSalesActive = salesEnabled && (!showDistributorToggle || distributorSource === "sales");

  const renderSourceRadio = (source: "geographical" | "sales") => {
    if (!showDistributorToggle) return null;
    return (
      <Radio
        size="small"
        checked={distributorSource === source}
        onChange={() => onDistributorSourceChange(source)}
        sx={{
          padding: 0,
          marginRight: "4px",
          "&.Mui-checked": {
            color: "#00A392"
          }
        }}
      />
    );
  };

  return (
    <Box 
      sx={{ 
        width: 'fit-content',
        maxWidth: '100%',
        backgroundColor: '#F3F4F6',
        border: '1px solid #E5E7EB',
        borderRadius: '8px',
        padding: '4px',  
        boxSizing: 'border-box'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: '8px',
          rowGap: '14px',
          width: '100%',
          flexWrap: 'nowrap',
          alignItems: 'center'
        }}
      >
        {selectedReport?.showDistributorType !== false && <DistributorTypeFilter
          value={getFilterValues('distributor_type')}
          options={getOptions('distributor_type')}
          onChange={(values) => onMultiFilterChange('distributor_type', values)}
          loading={isLoading('distributor_type')}
          enableSelectAll
          selectAllLabel="Select all types"
        />
}
        {selectedReport?.showDistributorDivision !== false && <DivisionFilter
          value={getFilterValues('distributor_division')}
          options={getOptions('distributor_division')}
          onChange={(values) => onMultiFilterChange('distributor_division', values)}
          loading={isLoading('distributor_division')}
          enableSelectAll
          selectAllLabel="Select all divisions"
        />}

        {geoEnabled && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {renderSourceRadio("geographical")}
            <HierarchyDropdown 
              label="Geography" 
              width={325}
              selectionCount={geoSelectionCount}
              drillDownPath={geoDrillDownPath}
              pathSummary={geoPathSummary}
              disabled={!isGeoActive}
              onBeforeClose={onGeoDropdownClose}
              isLoading={isHierarchySyncing}
            >
              <GeographicalHierarchyFilter
                selectedReport={selectedReport}
                filters={filters}
                optionsMap={optionsMap}
                loadingMap={loadingMap}
                onLevelChange={onGeoLevelChange}
                onValueChange={onGeoValueChange}
                onLoadLevels={onGeoLoadLevels}
                onNextLevel={onGeoNextLevel}
                onResetAll={onGeoResetAll}
                isLastLevel={geoIsLastLevel}
              />
            </HierarchyDropdown>
          </Box>
        )}

        {salesEnabled && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {renderSourceRadio("sales")}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'nowrap' }}>
              <HierarchyDropdown 
                label="Sales Hierarchy" 
                width={325}
                selectionCount={salesSelectionCount}
                drillDownPath={salesDrillDownPath}
                pathSummary={salesPathSummary}
                disabled={!isSalesActive}
                onBeforeClose={onSalesDropdownClose}
                isLoading={isHierarchySyncing}
              >
                <SalesHierarchyFilter
                  selectedReport={selectedReport}
                  filters={filters}
                  optionsMap={optionsMap}
                  loadingMap={loadingMap}
                  onLevelChange={onSalesLevelChange}
                  onValueChange={onSalesValueChange}
                  onLoadLevels={onSalesLoadLevels}
                  onNextLevel={onSalesNextLevel}
                  onResetAll={onSalesResetAll}
                />
              </HierarchyDropdown>
              {selectedReport?.distributorFilter?.enabled && onLoadDistributorOptions && selectedReport && (
                <DistributorFilter
                  selectedReport={selectedReport}
                  filters={filters}
                  optionsMap={optionsMap}
                  loadingMap={loadingMap}
                  onFilterChange={onFilterChange}
                  onMultiFilterChange={onMultiFilterChange}
                  onLoadOptions={onLoadDistributorOptions}
                  disabled={shouldDisableDistributorFilter}
                  disabledReason={distributorDisabledReason}
                  dropdownWidth={170}
                  enableSelectAll={true}
                  selectAllLabel="Select all distributors"
                  searchText={selectedReport.distributorFilter?.field ? searchTextMap[selectedReport.distributorFilter.field] : undefined}
                  onSearchChange={selectedReport.distributorFilter?.field && onFilterInputChange 
                    ? (value) => onFilterInputChange(selectedReport.distributorFilter!.field, value)
                    : undefined}
                />
              )}
            </Box>
          </Box>
        )}

        {!salesEnabled && selectedReport?.distributorFilter?.enabled && onLoadDistributorOptions && selectedReport && (
          <DistributorFilter
            selectedReport={selectedReport}
            filters={filters}
            optionsMap={optionsMap}
            loadingMap={loadingMap}
            onFilterChange={onFilterChange}
            onMultiFilterChange={onMultiFilterChange}
            enableSelectAll={true}
            selectAllLabel="Select all distributors"
            onLoadOptions={onLoadDistributorOptions}
            disabled={shouldDisableDistributorFilter}
            disabledReason={distributorDisabledReason}
            dropdownWidth={170}
            searchText={selectedReport.distributorFilter?.field ? searchTextMap[selectedReport.distributorFilter.field] : undefined}
            onSearchChange={selectedReport.distributorFilter?.field && onFilterInputChange 
              ? (value) => onFilterInputChange(selectedReport.distributorFilter!.field, value)
              : undefined}
          />
        )}

        {/* Separator line between distributorFilter and reset button */}
        {onReset && (
          <Divider
            orientation="vertical"
            flexItem
            sx={{
              height: '35px',
              borderRight: '1.5px dotted #7C7C7C',  // dotted line
              margin: '0 0px',
              opacity: 1 // ensure visibility
            }}
          />
        )}


        {/* Reset Button - After distributor dropdown */}
        {onReset && (
          <Button
            variant="outlined"
            onClick={onReset}
            startIcon={<Refresh sx={{ fontSize: '18px' }} />}
            sx={{
              textTransform: "none",
              borderColor: "#7C7C7C",
              color: "#7C7C7C",
              borderRadius: "8px",
              fontWeight: 600,
              fontSize: "14px",
              padding: "2px 8px",
              height: "33px",
              '&:hover': {
                borderColor: "#7C7C7C",
                color: "#7C7C7C",
                backgroundColor: 'transparent'
              }
            }}
          >
            Reset
          </Button>
        )}
        
      </Box>
    </Box>
  );
};

export default TopFilterBar;
